import HelpIcon from "@mui/icons-material/HelpOutlineRounded";
import type { TableCellProps } from "@mui/material";
import { Button, Stack, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import TableWithActions from "@entrilia/shared/components/TableWithActions";
import type { InvestorBankAccountAssignment } from "../../../../api/types/bankAccountTypes";
import UnavailableFundTooltip from "../common/UnavailableFundTooltip";
import useBankAccountsContext from "./useBankAccountsContext";

interface RowModel extends InvestorBankAccountAssignment {
  disabled?: boolean;
}

interface BodyRowProps {
  row: RowModel;
  onChangeAssignment?: (fundInvestorId: string, fundName: string, bankAccountId: string) => void;
}

const Cell = ({ children, ...props }: PropsWithChildren<TableCellProps>) => (
  <TableCell {...props} sx={{ py: 1.25 }}>
    {children}
  </TableCell>
);

const BodyRow = ({ row, onChangeAssignment }: BodyRowProps) => {
  const { bankAccounts, canUserEdit } = useBankAccountsContext();

  if (!canUserEdit) {
    return (
      <TableRow>
        <Cell>{row.fundName}</Cell>
        <Cell>{row.bankDescription}</Cell>
        <Cell />
      </TableRow>
    );
  }

  if (row.disabled) {
    return (
      <TableRow sx={(t) => ({ ".MuiTableCell-root": { color: t.palette.text.disabled } })}>
        <Cell>{row.fundName}</Cell>
        <Cell>
          <Stack direction="row" spacing={1}>
            <Typography>{row.bankDescription}</Typography>
            <UnavailableFundTooltip active>
              <HelpIcon />
            </UnavailableFundTooltip>
          </Stack>
        </Cell>
        <Cell />
      </TableRow>
    );
  }

  const otherBankAccountsExist = bankAccounts.some((account) => account.id !== row.bankAccountId);

  return (
    <TableRow>
      <Cell>{row.fundName}</Cell>
      <Cell>{row.bankDescription}</Cell>
      <Cell className="table-row-actions">
        {otherBankAccountsExist && (
          <Button
            variant="text"
            onClick={() => onChangeAssignment?.(row.fundInvestorId, row.fundName, row.bankAccountId)}
          >
            Change
          </Button>
        )}
      </Cell>
    </TableRow>
  );
};

interface Props {
  rows: RowModel[];
  onChangeAssignment: (fundInvestorId: string, fundName: string, bankAccountId: string) => void;
}

const FundAssignmentsTable = ({ rows, onChangeAssignment }: Props) => {
  return (
    <TableContainer>
      <TableWithActions stickyHeader>
        <TableHead>
          <TableRow>
            <Cell width="45%">Fund</Cell>
            <Cell width="45%">Bank Account</Cell>
            <Cell width="10%"></Cell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <BodyRow key={row.fundInvestorId} row={row} onChangeAssignment={onChangeAssignment} />
          ))}
        </TableBody>
      </TableWithActions>
    </TableContainer>
  );
};

export default FundAssignmentsTable;
